import { computed, h, defineComponent } from 'vue';
import ImageDrawing from '@/ui/components/inspectionReport/questions/ImageDrawing';
import { imageUrl as _imageUrl, ImageVersions } from '@/services/assets-api/assetsClient';
import { t } from '@/ui/i18n/i18n';

interface ImageAssetProps {
  photo: Record<string, string>;
  isThumbnail?: boolean;
  isLowQualityImages?: boolean;
}

export const ImageAsset = defineComponent<ImageAssetProps>({
  name: 'ImageAsset',
  props: ['photo', 'isThumbnail', 'isLowQualityImages'],
  setup(props, ctx) {
    const imageUrl = props.isLowQualityImages
      ? (id: string) => _imageUrl(id, ImageVersions.THUMB)
      : (id: string) => _imageUrl(id);

    const hasNoDrawingIdAndNoImageId = () => !props.photo.image_id && !props.photo.drawing_id;
    const hasDrawingAndNoImage = () => (!props.photo.image_id && !props.photo.image)
      && (props.photo.drawing_id && props.photo.drawing);
    const hasDrawingId = () => !!props.photo.drawing_id;
    const hasDrawing = () => !!props.photo.drawing;
    const hasImage = () => !!props.photo.image;
    const hasImageId = () => !!props.photo.image_id;

    const thumbnailClass = computed(() => props.isThumbnail ? 'inspection-report__image-drawing-thumbnail' : '');

    const renderImage = (backgroundImage: string | null, imageSrc: string) => h('img', {
      class: `${thumbnailClass.value}`,
      style: backgroundImage ? { 'background-image': backgroundImage } : {},
      attrs: {
        src: imageSrc,
      },
      // @ts-ignore
      on: ctx.listeners,
    });

    return function render() {
      if (hasDrawingId() && !hasDrawing() && !hasImage() && !hasImageId()) {
        // some signatures only has `drawing_id` on them
        const imageSrc = imageUrl(props.photo.drawing_id);
        return renderImage(null, imageSrc);
      }

      if (props.photo.image_id) {
        //full images from assets service
        const backgroundImage = props.photo.drawing_id ? `url(${imageUrl(props.photo.image_id)})` : null;
        const imageSrc = props.photo.drawing_id ? imageUrl(props.photo.drawing_id) : imageUrl(props.photo.image_id);

        return renderImage(backgroundImage, imageSrc);
      }

      if (hasNoDrawingIdAndNoImageId() || hasDrawingAndNoImage()) {
        //legacy binaries only - it will happen when the user is creating a form template
        //and it is necessary to handle binaries before sending them to the asset service
        return h(ImageDrawing, {
          props: {
            image: props.photo.image || null,
            drawing: props.photo.drawing || null,
            isThumbnail: props.isThumbnail || false,
          },
          // @ts-ignore
          on: ctx.listeners,
        });
      }

      return h('div', t('common.no-image'));
    };
  },
});
